// src/features/recipes/CookingMode.tsx
import React, { useEffect, useState } from "react";
import {
  Box,
  Typography,
  Container,
  Button,
  LinearProgress,
  List,
  ListItem,
  ListItemText,
  Paper,
  IconButton,
  CircularProgress,
  Alert,
  AlertTitle,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
} from "@mui/material";
import {
  ArrowBack,
  ArrowForward,
  CheckCircle,
  Close,
  SwapHoriz,
} from "@mui/icons-material";
import { useParams, useNavigate, useSearchParams } from "react-router-dom";
import { getRecipeById } from "../../services/api";
import api from "../../services/api";
import dayjs from "dayjs";

interface Step {
  step_id?: number;
  step_number: number;
  description: string;
  image_url?: string;
}

interface Ingredient {
  ingredient_id?: number;
  name: string;
  quantity?: number;
  unit?: string;
}

const CookingMode: React.FC = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const mealId = searchParams.get('meal_id'); // Optional: for marking meal as complete

  const [recipe, setRecipe] = useState<any | null>(null);
  const [loading, setLoading] = useState(true);
  const [currentStep, setCurrentStep] = useState(0);
  const [completing, setCompleting] = useState(false);
  const [error, setError] = useState('');
  const [showCompleteDialog, setShowCompleteDialog] = useState(false);
  const [mealData, setMealData] = useState<any | null>(null);

  useEffect(() => {
    const load = async () => {
      try {
        const data = await getRecipeById(Number(id), mealId ? Number(mealId) : undefined);
        setRecipe(data);

        // Load meal data if meal_id is provided (for leftover status, etc.)
        if (mealId) {
          try {
            const mealResponse = await api.get(`/meals/${mealId}`);
            setMealData(mealResponse.data);
          } catch (err) {
            console.error('Failed to load meal data', err);
          }
        }
      } catch (err) {
        console.error("Failed to load recipe", err);
        setError("Failed to load recipe");
      } finally {
        setLoading(false);
      }
    };
    load();
  }, [id, mealId]);

  const handleNext = () => {
    if (recipe?.steps && currentStep < recipe.steps.length - 1) {
      setCurrentStep(currentStep + 1);
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }
  };

  const handlePrevious = () => {
    if (currentStep > 0) {
      setCurrentStep(currentStep - 1);
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }
  };

  const handleComplete = async () => {
    if (!mealId) {
      // No meal_id provided, just show success and go back
      setShowCompleteDialog(true);
      return;
    }

    setCompleting(true);
    try {
      await api.patch(`/meals/${mealId}/complete`);
      setShowCompleteDialog(true);
    } catch (err: any) {
      console.error("Failed to mark meal as complete", err);
      setError(err.response?.data?.message || "Failed to mark meal as complete");
    } finally {
      setCompleting(false);
    }
  };

  const handleCloseDialog = () => {
    setShowCompleteDialog(false);
    navigate('/dashboard'); // Go back to dashboard
  };

  const handleExit = () => {
    navigate(-1);
  };

  if (loading) {
    return (
      <Box mt={6} textAlign="center">
        <CircularProgress />
      </Box>
    );
  }

  if (error || !recipe) {
    return (
      <Container sx={{ mt: 4 }}>
        <Alert severity="error">{error || "Recipe not found"}</Alert>
        <Button onClick={() => navigate(-1)} sx={{ mt: 2 }}>
          Go Back
        </Button>
      </Container>
    );
  }

  const steps: Step[] = recipe.steps || [];
  const ingredients: Ingredient[] = recipe.ingredients || [];
  const totalSteps = steps.length;
  const progress = totalSteps > 0 ? ((currentStep + 1) / totalSteps) * 100 : 0;
  const isLastStep = currentStep === totalSteps - 1;
  const currentStepData = steps[currentStep];
  const isAlreadyCompleted = mealData?.status === 'completed';

  return (
    <Box sx={{ minHeight: '100vh', bgcolor: 'grey.50', pb: 3 }}>
      {/* Header with progress */}
      <Paper
        elevation={2}
        sx={{
          position: 'sticky',
          top: 0,
          zIndex: 10,
          borderRadius: 0,
        }}
      >
        <Box sx={{ p: 2 }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
            <Typography variant="h6" sx={{ fontWeight: 'bold' }}>
              {recipe.name}
            </Typography>
            <IconButton onClick={handleExit} size="small">
              <Close />
            </IconButton>
          </Box>

          {/* Completion Banner */}
          {isAlreadyCompleted && mealData?.completed_at && (
            <Alert severity="success" sx={{ mb: 2 }} icon={<CheckCircle />}>
              <Typography variant="body2" sx={{ fontWeight: 500 }}>
                ✓ You cooked this on {dayjs(mealData.completed_at).format('MMMM D, YYYY [at] h:mm A')}
              </Typography>
            </Alert>
          )}

          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            <Typography variant="body2" color="text.secondary" sx={{ minWidth: 80 }}>
              Step {currentStep + 1} of {totalSteps}
            </Typography>
            <LinearProgress
              variant="determinate"
              value={progress}
              sx={{ flexGrow: 1, height: 8, borderRadius: 4 }}
            />
          </Box>
        </Box>
      </Paper>

      {/* Main Content */}
      <Container maxWidth="md" sx={{ mt: 3 }}>
        {/* Step Image (if available) */}
        {(currentStepData?.image_url || recipe.image_url) && (
          <Box sx={{ mb: 3, position: 'relative' }}>
            <Box
              component="img"
              src={currentStepData?.image_url || recipe.image_url}
              alt={`Step ${currentStep + 1}`}
              onError={(e) => {
                (e.currentTarget as HTMLImageElement).style.display = 'none';
              }}
              sx={{
                width: "100%",
                maxHeight: 300,
                objectFit: "cover",
                borderRadius: 2,
                boxShadow: 2
              }}
            />
            {/* Pexels Attribution - only for main recipe image */}
            {!currentStepData?.image_url && recipe.image_photographer && (
              <Box
                sx={{
                  position: 'absolute',
                  bottom: 8,
                  right: 8,
                  backgroundColor: 'rgba(0, 0, 0, 0.6)',
                  color: 'white',
                  padding: '4px 8px',
                  borderRadius: 1,
                  fontSize: '11px'
                }}
              >
                📷{' '}
                {recipe.image_photographer_url ? (
                  <a
                    href={recipe.image_photographer_url}
                    target="_blank"
                    rel="noopener noreferrer"
                    style={{ color: 'white', textDecoration: 'none' }}
                  >
                    {recipe.image_photographer}
                  </a>
                ) : (
                  recipe.image_photographer
                )}{' '}
                via Pexels
              </Box>
            )}
          </Box>
        )}

        {/* Modular recipe: protein selections with prep instructions */}
        {mealData?.protein_selections?.length > 0 && (
          <Alert severity="info" icon={<SwapHoriz />} sx={{ mb: 3 }}>
            <AlertTitle>Protein Options</AlertTitle>
            {mealData.protein_selections.map((ps: any, i: number) => (
              <Box key={i} sx={{ mb: i < mealData.protein_selections.length - 1 ? 2 : 0 }}>
                <Typography variant="subtitle2" color="primary">{ps.member_name}</Typography>
                <Typography variant="body2" sx={{ fontWeight: 'bold' }}>
                  {ps.option_name} — {ps.quantity_per_serving} {ps.unit}
                </Typography>
                <Typography variant="body2" color="text.secondary">{ps.prep_instructions}</Typography>
              </Box>
            ))}
          </Alert>
        )}

        {/* Structured adaptations (non-modular mixed-diet) */}
        {mealData?.adaptations?.length > 0 && (
          <Alert severity="info" icon={<SwapHoriz />} sx={{ mb: 3 }}>
            <AlertTitle>Adaptations</AlertTitle>
            {mealData.adaptations.map((adapt: any, i: number) => (
              <Box key={i} sx={{ mb: 2 }}>
                <Typography variant="subtitle2" color="primary">
                  {adapt.members.join(' & ')}
                </Typography>
                <Typography variant="body2" sx={{ fontWeight: 'bold' }}>
                  {adapt.action === 'replace'
                    ? `${adapt.substitute} instead of ${adapt.original}`
                    : adapt.action === 'add'
                    ? `Add ${adapt.substitute}`
                    : `Remove ${adapt.original}`}
                  {adapt.quantity ? ` (${adapt.quantity})` : ''}
                </Typography>
                <List dense disablePadding sx={{ pl: 1 }}>
                  {adapt.steps.map((step: string, si: number) => (
                    <ListItem key={si} disablePadding sx={{ py: 0 }}>
                      <ListItemText primary={`${si + 1}. ${step}`} primaryTypographyProps={{ variant: 'body2', color: 'text.secondary' }} />
                    </ListItem>
                  ))}
                </List>
                <Alert severity="warning" variant="outlined" sx={{ mt: 1, py: 0.5 }}>
                  <Typography variant="caption">{adapt.plating_note}</Typography>
                </Alert>
              </Box>
            ))}
          </Alert>
        )}

        {/* Legacy fallback: plain notes + ingredient overrides */}
        {!mealData?.adaptations?.length && !mealData?.protein_selections?.length && mealData?.notes && (
          <Alert severity="info" icon={<SwapHoriz />} sx={{ mb: 3 }}>
            <AlertTitle>Meal Adaptations</AlertTitle>
            <Typography variant="body2" sx={{ whiteSpace: 'pre-line' }}>
              {mealData.notes}
            </Typography>
            {mealData.ingredient_overrides?.length > 0 && (
              <List dense sx={{ mt: 1 }}>
                {mealData.ingredient_overrides.map((ov: any, i: number) => (
                  <ListItem key={i} disablePadding>
                    <ListItemText
                      primary={
                        ov.action === 'add' ? `+ Add ${ov.quantity || ''} ${ov.unit || ''} ${ov.ingredient}`.trim()
                        : ov.action === 'remove' ? `− Remove ${ov.ingredient}`
                        : `↕ Scale ${ov.ingredient}`
                      }
                      secondary={ov.reason}
                    />
                  </ListItem>
                ))}
              </List>
            )}
          </Alert>
        )}

        {/* Step Instructions */}
        <Paper elevation={1} sx={{ p: 3, mb: 3 }}>
          <Typography variant="h5" color="primary" gutterBottom sx={{ fontWeight: 'bold' }}>
            Step {currentStepData?.step_number || currentStep + 1}
          </Typography>
          <Typography variant="body1" sx={{ fontSize: '1.1rem', lineHeight: 1.8 }}>
            {currentStepData?.description}
          </Typography>
        </Paper>

        {/* Ingredients List (shown on every step for reference) */}
        <Paper elevation={1} sx={{ p: 3, mb: 3 }}>
          <Typography variant="h6" gutterBottom sx={{ fontWeight: 'bold' }}>
            Ingredients Needed
          </Typography>
          <List dense>
            {ingredients.filter((ing: any) => !ing.is_removed).map((ing: any, idx: number) => {
              const override = !ing.is_added ? mealData?.ingredient_overrides?.find(
                (ov: any) => ing.name.toLowerCase().includes(ov.ingredient.toLowerCase())
              ) : undefined;
              const displayQty = ing.display_quantity ?? ing.scaled_quantity ?? ing.quantity;
              const displayUnit = ing.display_unit ?? ing.unit;
              return (
                <ListItem key={idx} sx={{ py: 0.5 }}>
                  <ListItemText
                    primary={
                      <Typography variant="body2" sx={
                        ing.is_added ? { color: 'success.main', fontWeight: 'bold' }
                        : override ? { color: 'info.main', fontWeight: 'bold' }
                        : undefined
                      }>
                        {ing.is_added ? '+ ' : ''}
                        <strong>{displayQty ?? ""} {displayUnit ?? ""}</strong> {ing.name}
                        {ing.is_added && ing.reason ? ` (${ing.reason})` : ''}
                        {override && (
                          override.action === 'add' ? ' ✚' : override.action === 'remove' ? ' ✕' : ' ↕'
                        )}
                      </Typography>
                    }
                  />
                </ListItem>
              );
            })}
          </List>
        </Paper>

        {/* Navigation Buttons */}
        <Box sx={{ display: 'flex', gap: 2, mb: 2 }}>
          <Button
            variant="outlined"
            size="large"
            startIcon={<ArrowBack />}
            onClick={handlePrevious}
            disabled={currentStep === 0}
            fullWidth
          >
            Previous
          </Button>

          {!isLastStep ? (
            <Button
              variant="contained"
              size="large"
              endIcon={<ArrowForward />}
              onClick={handleNext}
              fullWidth
            >
              Next Step
            </Button>
          ) : (
            <Button
              variant="contained"
              color="success"
              size="large"
              startIcon={<CheckCircle />}
              onClick={handleComplete}
              disabled={completing}
              fullWidth
            >
              {completing ? (
                <CircularProgress size={24} />
              ) : isAlreadyCompleted ? (
                "Mark as Cooked Again"
              ) : (
                "Complete!"
              )}
            </Button>
          )}
        </Box>
      </Container>

      {/* Completion Dialog */}
      <Dialog open={showCompleteDialog} onClose={handleCloseDialog}>
        <DialogTitle sx={{ textAlign: 'center', pt: 4 }}>
          <CheckCircle sx={{ fontSize: 60, color: 'success.main', mb: 2 }} />
          <Typography variant="h5" sx={{ fontWeight: 'bold' }}>
            Great Job!
          </Typography>
        </DialogTitle>
        <DialogContent>
          <Typography variant="body1" sx={{ textAlign: 'center' }}>
            You've completed cooking <strong>{recipe.name}</strong>!
          </Typography>
          {mealId && (
            <Typography variant="body2" color="text.secondary" sx={{ textAlign: 'center', mt: 1 }}>
              This meal has been marked as complete in your meal plan.
            </Typography>
          )}
        </DialogContent>
        <DialogActions sx={{ justifyContent: 'center', pb: 3 }}>
          <Button
            variant="contained"
            onClick={handleCloseDialog}
            size="large"
          >
            Done
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default CookingMode;
